import { getHeaders } from "../request-headers.ts";
import type {
  AdminReviewRequestItem,
  AdminReviewRequestsResponse,
  AdminReviewRequestsTableResponse,
  AiOptimizedDescriptionResponse,
  BatchRejectReviewRequestsInput,
  CompleteReviewRequestInput,
  GetAdminReviewRequestsParams,
  GetAdminReviewRequestsTableParams,
  GetLedgerParams,
  GetReviewRequestHistoryParams,
  LedgerResponse,
  OkResponse,
  PointsResponse,
  RejectReviewRequestInput,
  RejectReviewRequestsBeforeInput,
  RejectReviewRequestsBeforeResponse,
  ReviewRequestHistoryResponse,
  ReviewRequestInput,
  ReviewRequestResponse,
  SubmitReviewRequestResponse,
} from "../../model";

import { cnapexApiFetch } from "../../../fetcher.ts";

export const getGetAdminReviewRequestsUrl = (params: GetAdminReviewRequestsParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/admin/review-requests?${stringifiedParams}`
    : `/api/admin/review-requests`;
};

export const getAdminReviewRequests = async (
  params: GetAdminReviewRequestsParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<AdminReviewRequestsResponse> => {
  return cnapexApiFetch<AdminReviewRequestsResponse>(getGetAdminReviewRequestsUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getBatchRejectReviewRequestsUrl = () => {
  return `/api/admin/review-requests/batch-reject`;
};

export const batchRejectReviewRequests = async (
  batchRejectReviewRequestsInput: BatchRejectReviewRequestsInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getBatchRejectReviewRequestsUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(batchRejectReviewRequestsInput),
  });
};

export const getRejectReviewRequestsBeforeCreatedAtUrl = () => {
  return `/api/admin/review-requests/reject-before-created-at`;
};

export const rejectReviewRequestsBeforeCreatedAt = async (
  rejectReviewRequestsBeforeInput: RejectReviewRequestsBeforeInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<RejectReviewRequestsBeforeResponse> => {
  return cnapexApiFetch<RejectReviewRequestsBeforeResponse>(
    getRejectReviewRequestsBeforeCreatedAtUrl(),
    {
      ...options,
      method: "POST",
      headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
      body: JSON.stringify(rejectReviewRequestsBeforeInput),
    },
  );
};

export const getGetAdminReviewRequestsTableUrl = (params?: GetAdminReviewRequestsTableParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/admin/review-requests/table?${stringifiedParams}`
    : `/api/admin/review-requests/table`;
};

export const getAdminReviewRequestsTable = async (
  params?: GetAdminReviewRequestsTableParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<AdminReviewRequestsTableResponse> => {
  return cnapexApiFetch<AdminReviewRequestsTableResponse>(
    getGetAdminReviewRequestsTableUrl(params),
    {
      ...options,
      method: "GET",
    },
  );
};

export const getGetAdminReviewRequestUrl = (id: string) => {
  return `/api/admin/review-requests/${id}`;
};

export const getAdminReviewRequest = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<AdminReviewRequestItem> => {
  return cnapexApiFetch<AdminReviewRequestItem>(getGetAdminReviewRequestUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getCompleteReviewRequestUrl = (id: string) => {
  return `/api/admin/review-requests/${id}/complete`;
};

export const completeReviewRequest = async (
  id: string,
  completeReviewRequestInput: CompleteReviewRequestInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getCompleteReviewRequestUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(completeReviewRequestInput),
  });
};

export const getRejectReviewRequestUrl = (id: string) => {
  return `/api/admin/review-requests/${id}/reject`;
};

export const rejectReviewRequest = async (
  id: string,
  rejectReviewRequestInput: RejectReviewRequestInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getRejectReviewRequestUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(rejectReviewRequestInput),
  });
};

export const getStartReviewRequestUrl = (id: string) => {
  return `/api/admin/review-requests/${id}/start`;
};

export const startReviewRequest = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getStartReviewRequestUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getGetLedgerUrl = (params?: GetLedgerParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0 ? `/api/ledger?${stringifiedParams}` : `/api/ledger`;
};

export const getLedger = async (
  params?: GetLedgerParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<LedgerResponse> => {
  return cnapexApiFetch<LedgerResponse>(getGetLedgerUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getSubmitReviewRequestUrl = () => {
  return `/api/review-requests`;
};

export const submitReviewRequest = async (
  reviewRequestInput: ReviewRequestInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<SubmitReviewRequestResponse> => {
  return cnapexApiFetch<SubmitReviewRequestResponse>(getSubmitReviewRequestUrl(), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewRequestInput),
  });
};

export const getGetReviewRequestHistoryUrl = (params?: GetReviewRequestHistoryParams) => {
  const normalizedParams = new URLSearchParams();

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined) {
      normalizedParams.append(key, value === null ? "null" : String(value));
    }
  });

  const stringifiedParams = normalizedParams.toString();

  return stringifiedParams.length > 0
    ? `/api/review-requests/history?${stringifiedParams}`
    : `/api/review-requests/history`;
};

export const getReviewRequestHistory = async (
  params?: GetReviewRequestHistoryParams,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ReviewRequestHistoryResponse> => {
  return cnapexApiFetch<ReviewRequestHistoryResponse>(getGetReviewRequestHistoryUrl(params), {
    ...options,
    method: "GET",
  });
};

export const getGetReviewRequestUrl = (id: string) => {
  return `/api/review-requests/${id}`;
};

export const getReviewRequest = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<ReviewRequestResponse> => {
  return cnapexApiFetch<ReviewRequestResponse>(getGetReviewRequestUrl(id), {
    ...options,
    method: "GET",
  });
};

export const getUpdateReviewRequestUrl = (id: string) => {
  return `/api/review-requests/${id}`;
};

export const updateReviewRequest = async (
  id: string,
  reviewRequestInput: ReviewRequestInput,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<SubmitReviewRequestResponse> => {
  return cnapexApiFetch<SubmitReviewRequestResponse>(getUpdateReviewRequestUrl(id), {
    ...options,
    method: "POST",
    headers: { "Content-Type": "application/json", ...getHeaders(options?.headers) },
    body: JSON.stringify(reviewRequestInput),
  });
};

export const getGenerateAiOptimizedDescriptionForReviewRequestUrl = (id: string) => {
  return `/api/review-requests/${id}/ai-optimized-description`;
};

export const generateAiOptimizedDescriptionForReviewRequest = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<AiOptimizedDescriptionResponse> => {
  return cnapexApiFetch<AiOptimizedDescriptionResponse>(
    getGenerateAiOptimizedDescriptionForReviewRequestUrl(id),
    {
      ...options,
      method: "POST",
    },
  );
};

export const getCancelReviewRequestUrl = (id: string) => {
  return `/api/review-requests/${id}/cancel`;
};

export const cancelReviewRequest = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getCancelReviewRequestUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getRestoreReviewRequestUrl = (id: string) => {
  return `/api/review-requests/${id}/restore`;
};

export const restoreReviewRequest = async (
  id: string,
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<OkResponse> => {
  return cnapexApiFetch<OkResponse>(getRestoreReviewRequestUrl(id), {
    ...options,
    method: "POST",
  });
};

export const getGetMyPointsUrl = () => {
  return `/api/users/me/points`;
};

export const getMyPoints = async (
  options?: Parameters<typeof cnapexApiFetch>[1],
): Promise<PointsResponse> => {
  return cnapexApiFetch<PointsResponse>(getGetMyPointsUrl(), {
    ...options,
    method: "GET",
  });
};
